import {Kpi} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {Card, CardTitle, Loader} from '@hconnect/uikit/src/lib2'
import {Add} from '@mui/icons-material'
import {Box, Button, Stack} from '@mui/material'
import React, {FC, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {UpmMappingTypes} from '../../../../../common/enums/enums'
import {useSelectedItem} from '../../../../providers/SelectedItemProvider'
import {useJanusSpecificKpis} from '../../hooks/useJanusSpecificKpis'
import {useJanusSpecificKpisByDefinition} from '../../hooks/useJanusSpecificKpisByDefinition'
import {JanusSpecificKpi} from '../../types'

import {JanusKeyFiguresForm} from './JanusKeyFiguresForm'

import {useGetPlantUpmId} from '@settings/modules/plant-setup/hooks/upmEntityConfig/useGetPlantUpmId'

type JanusKeyFiguresCardProps = {
  kpi: Kpi
}

type KpiUnit = {
  id: string
  unit: string
  abbreviation: string
  siUnitType?: string
}

const kpiUnitTypeData: KpiUnit[] = [
  {id: '1', unit: 'Ton', abbreviation: 't', siUnitType: 'TO'},
  {id: '2', unit: 'Time', abbreviation: 'h', siUnitType: 'H'},
  {id: '4', unit: 'Percentage', abbreviation: '%', siUnitType: '%'},
  {id: '5', unit: 'Gigajoule per ton', abbreviation: 'GJ/t'}
]

const findKpiUnitIdByAbbreviation = (abbreviation: string | null): string | null =>
  kpiUnitTypeData.find((unit) => unit.abbreviation === abbreviation)?.id ?? null

export const JanusKeyFiguresCard: FC<JanusKeyFiguresCardProps> = ({kpi}) => {
  const {t} = useTranslation()
  const unitId = useMemo(() => findKpiUnitIdByAbbreviation(kpi.unit), [kpi.unit])

  const [newKeyFigure, setNewKeyFigure] = useState<Partial<JanusSpecificKpi> | undefined>()
  const {selectedItem} = useSelectedItem()
  const {data: plant} = useGetPlantUpmId()
  const {
    data: plantKpis,
    isLoading: isLoadingPlantKpis,
    isFetching: isFetchingPlantKpis
  } = useJanusSpecificKpis(
    plant!.upmId,
    unitId!,
    UpmMappingTypes[selectedItem!.itemType],
    selectedItem!.upmId
  )
  const {data: kpiKeyFigures, isLoading: isLoadingKpiKeyFigures} = useJanusSpecificKpisByDefinition(
    plant!.upmId,
    kpi.kpiDefinitionId
  )

  const isLoading = isLoadingPlantKpis || isLoadingKpiKeyFigures || isFetchingPlantKpis

  const definitions = useMemo(() => {
    const kpiJanusDefinitions = kpiKeyFigures ?? []
    return [...kpiJanusDefinitions, ...(newKeyFigure ? [newKeyFigure] : [])]
  }, [kpiKeyFigures, newKeyFigure])

  if (isLoading) {
    return <Loader />
  }

  return (
    <Card
      headerContent={
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <CardTitle sx={{mb: 0}}>{t('kpiCalculationSettings.label.janusKeyFigures')}</CardTitle>
          <Box sx={{display: 'flex', gap: 1.5}}>
            <Button
              variant="text"
              color="primary"
              disabled={!!newKeyFigure}
              onClick={() => {
                setNewKeyFigure({displayName: ''})
              }}
              startIcon={<Add />}
              {...dataTestId('add-janus-key-figure-button')}
            >
              {t('kpiCalculationSettings.action.add')}
            </Button>
          </Box>
        </Box>
      }
      sx={{boxShadow: 'none', pt: 0}}
      {...dataTestId('janus-key-figures-card')}
    >
      <Stack spacing={2}>
        {definitions?.map((keyFigure) => (
          <JanusKeyFiguresForm
            key={keyFigure.id}
            unitId={unitId}
            upmId={plant!.upmId}
            kpi={kpi}
            plantKpis={plantKpis}
            keyFigure={keyFigure}
            setNewKeyFigure={setNewKeyFigure}
          />
        ))}
      </Stack>
    </Card>
  )
}
